import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useAppSelector } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';
import CountryPicker from '../../components/countryPicker/CountryPicker';
import Countries from '../../components/countryPicker/Countries';

export interface UserInfoFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phonePrefixCountry: number;
  address: string;
  city?: string;
  zipcode?: string;
  country?: number;
  password?: string;
  passwordConfirm?: string;
}

interface UserInfoFormProps {
  onFormChange?: (isValid: boolean) => void;
  formData?: UserInfoFormData;
  setFormData?: (data: UserInfoFormData) => void;
}

const UserInfoForm = forwardRef(({ onFormChange, formData, setFormData }: UserInfoFormProps, ref) => {
  useAppSelector(selectLanguage);

  const { accountState } = useAppSelector((state) => state.priceCalculator);

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    address: false,
    city: false,
    zipcode: false,
    country: false,
    password: false,
    passwordConfirm: false,
  });

  const isValid = (currentErrors = errors): boolean => {
    // Only required fields must be valid
    return (
      !currentErrors.firstName &&
      !currentErrors.lastName &&
      !currentErrors.email &&
      !currentErrors.phone &&
      !currentErrors.address &&
      !currentErrors.city &&
      !currentErrors.zipcode &&
      !currentErrors.country &&
      !currentErrors.password &&
      !currentErrors.passwordConfirm
    );
  };

  // Expose the validate function to the parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(formData?.email || "");
      const passwordValid = (formData?.password || "").length > 6;
      const passwordsMatch = formData?.password === formData?.passwordConfirm;
      const newErrors = {
        firstName: !formData?.firstName,
        lastName: !formData?.lastName,
        email: !isValidEmail,
        phone: !formData?.phone,
        address: !formData?.address,
        city: !formData?.city,
        zipcode: !formData?.zipcode,
        country: !formData?.country,
        password: !passwordValid,
        passwordConfirm: !passwordsMatch || !passwordValid,
      };
      setErrors(newErrors);
      return isValid(newErrors);
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement ?? e.target as HTMLSelectElement;
    if (setFormData) {
      setFormData({ ...formData, [name]: value } as UserInfoFormData);
    }

    let updatedErrors = { ...errors };

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      updatedErrors.email = !emailRegex.test(value);
    } else if (name === "password") {
      updatedErrors.password = value.length <= 6;
      updatedErrors.passwordConfirm = value !== (formData?.passwordConfirm || "");
    } else if (name === "passwordConfirm") {
      updatedErrors.passwordConfirm = value !== (formData?.password || "");
    } else if (["firstName", "lastName", "phone", "address", "city", "zipcode", "country"].includes(name)) {
      updatedErrors[name as keyof typeof errors] = !value;
    }
    // mobilePhone is not required, so no error handling

    setErrors(updatedErrors);
    if (onFormChange) {
      onFormChange(isValid(updatedErrors));
    }
  };

  return (
    <form className="p-4">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* First Name */}
        <div>
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.firstName}
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData?.firstName || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${errors.firstName ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder={strings.form.contactData.firstNamePlaceholder}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.firstName })}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.lastName}
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData?.lastName || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${errors.lastName ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder={strings.form.contactData.lastNamePlaceholder}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.lastName })}</p>
          )}
        </div>

        {/* Email */}
        {accountState !== 'loggedIn' && (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {strings.form.contactData.email}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData?.email || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"
                } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={strings.form.contactData.emailPlaceholder}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">
                {strings.formatString(strings.form.valuemissing, {
                  ValueName: strings.form.contactData.email,
                })}{" "}
              </p>
            )}
          </div>
        )}
        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.phone}
          </label>
          <div className="flex items-center gap-2">
            <CountryPicker
              visibleText="COU_VORWAHL"
              name="phonePrefixCountry"
              className="mb-0 min-w-25"
              selectedCountry={Countries.find(country => country.COU_ID === formData?.phonePrefixCountry) || Countries.find(CountryPicker => CountryPicker.COU_ID === 138) /* Default to Germany if no country is selected */ || Countries[0]}
              onCountryChange={handleChange}
            />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData?.phone || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${errors.phone ? "border-red-500" : "border-gray-300"
                } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder={strings.form.contactData.phonePlaceholder}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.phone })}</p>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="sm:col-span-2">
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.address}
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData?.address || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${errors.address ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder={strings.form.contactData.addressPlaceholder}
          />
          {errors.address && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.address })}</p>
          )}
        </div>

        {/* City */}
        <div>
          <label
            htmlFor="city"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.city}
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData?.city || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${errors.city ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder="Ihre Stadt"
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.city })}</p>
          )}
        </div>

        {/* Zipcode */}
        <div>
          <label
            htmlFor="zipcode"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.zip}
          </label>
          <input
            type="text"
            id="zipcode"
            name="zipcode"
            value={formData?.zipcode || ""}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border ${errors.zipcode ? "border-red-500" : "border-gray-300"
              } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
            placeholder="Postleitzahl"
          />
          {errors.zipcode && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.zip })}</p>
          )}
        </div>

        {/* Country */}
        <div>
          <label
            htmlFor="country"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
          >
            {strings.form.contactData.country}
          </label>
          <CountryPicker
            name="country"
            visibleText="COU_NAME"
            selectedCountry={Countries.find(country => country.COU_ID === formData?.country) || Countries.find(CountryPicker => CountryPicker.COU_ID === 138) /* Default to Germany if no country is selected */ || Countries[0]}
            onCountryChange={handleChange}
          />
          {errors.country && (
            <p className="mt-1 text-sm text-red-500">{strings.formatString(strings.form.valuemissing, { ValueName: strings.form.contactData.country })}</p>
          )}
        </div>

        {/* Password */}
        {accountState !== 'loggedIn' && (
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {strings.form.contactData.password}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData?.password || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${errors.password ? "border-red-500" : "border-gray-300"
                } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder="Passwort (mind. 7 Zeichen)"
              autoComplete="new-password"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">
                {strings.form.contactData.passwordLength}
              </p>
            )}
          </div>)}

        {/* Password Confirm */}
        {accountState !== 'loggedIn' && (
          <div>
            <label
              htmlFor="passwordConfirm"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left"
            >
              {strings.form.contactData.passwordConfirm}
            </label>
            <input
              type="password"
              id="passwordConfirm"
              name="passwordConfirm"
              value={formData?.passwordConfirm || ""}
              onChange={handleChange}
              className={`mt-1 block w-full rounded-md border ${errors.passwordConfirm ? "border-red-500" : "border-gray-300"
                } shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white px-4 py-2 text-lg`}
              placeholder="Passwort wiederholen"
              autoComplete="new-password"
            />
            {errors.passwordConfirm && (
              <p className="mt-1 text-sm text-red-500">
                {strings.form.contactData.passwordMatch}
              </p>
            )}
          </div>)}
      </div>
    </form>
  );
});

export default UserInfoForm;